import React, { useState, useRef, useEffect, useCallback } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import axios from 'axios';
import {
  Building2, Users, ChevronLeft, ChevronRight, LogOut, KeyRound, TrendingUp,
  UserCircle, FlaskConical, Search, X, Inbox, Syringe, Smartphone,
  GraduationCap, Recycle, Archive, Bug, Copy,
} from 'lucide-react';
import { NAV_GROUPS, activeGroupKey } from '@/components/navConfig';
import SubTabBar from '@/components/SubTabBar';
import SetPasswordDialog from '@/components/SetPasswordDialog';

const API = '/api';

const TYPE_CONFIG = {
  biotech: { label: 'Biotech', color: 'bg-sky-100 text-sky-700', icon: Building2 },
  biotech_academic: { label: 'Academic', color: 'bg-indigo-100 text-indigo-700', icon: GraduationCap },
  biotech_ndds: { label: 'NDDS', color: 'bg-rose-100 text-rose-700', icon: Syringe },
  biotech_dtx: { label: 'DTx', color: 'bg-purple-100 text-purple-700', icon: Smartphone },
  biotech_platform: { label: 'Platform', color: 'bg-cyan-100 text-cyan-700', icon: FlaskConical },
  asset: { label: 'Asset', color: 'bg-violet-100 text-violet-700', icon: FlaskConical },
  pharma_contact: { label: 'Pharma Contact', color: 'bg-emerald-100 text-emerald-700', icon: Users },
  pharma_company: { label: 'Pharma Co.', color: 'bg-teal-100 text-teal-700', icon: Building2 },
  biotech_contact: { label: 'Biotech Contact', color: 'bg-blue-100 text-blue-700', icon: UserCircle },
  // In-staging hits — surface companies that exist in /imports staging but
  // haven't been committed to the live CRM yet. Important for users at a
  // conference who uploaded a roster and want to find a name on-the-spot.
  staging: { label: 'In Staging', color: 'bg-orange-100 text-orange-700', icon: Inbox },
  // Archived hits — companies that got archived from biotech_companies /
  // company_profiles via the Duplicates Inspector. Rows still exist in
  // `archived_companies` and can be restored via /duplicates. Feb 2026
  // — user "even the email isn't searchable" incident.
  archived: { label: 'Archived', color: 'bg-slate-200 text-slate-700', icon: Archive },
};

export default function Layout({ children }) {
  const [collapsed, setCollapsed] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Super search state
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [searchHistory, setSearchHistory] = useState([]);
  const searchInputRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const searchContainerRef = useRef(null);

  // Search Debug (admin-only) — pops the raw resolver JSON for the
  // current query so we can diagnose canonical-linking edge cases
  // without ever leaving the browser. Feb 2026 · added after the
  // Immunochem / vshifrin@ic-rx.com routing incident recurred across
  // multiple sessions and needed shell access to fix each time.
  const [debugOpen, setDebugOpen] = useState(false);
  const [debugData, setDebugData] = useState(null);
  const [debugLoading, setDebugLoading] = useState(false);
  const [debugError, setDebugError] = useState(null);
  const isAdmin = user?.role === 'admin';

  const openSearchDebug = useCallback(async () => {
    const q = searchQuery.trim();
    if (q.length < 2) return;
    setDebugOpen(true);
    setDebugLoading(true);
    setDebugError(null);
    setDebugData(null);
    try {
      const res = await axios.get(`${API}/search/debug?q=${encodeURIComponent(q)}`, { withCredentials: true });
      setDebugData(res.data);
    } catch (e) {
      setDebugError(e?.response?.data?.detail || e.message || 'Failed to load debug data');
    } finally {
      setDebugLoading(false);
    }
  }, [searchQuery]);

  const copyDebugJson = useCallback(async () => {
    if (!debugData) return;
    try {
      await navigator.clipboard.writeText(JSON.stringify(debugData, null, 2));
    } catch { /* clipboard blocked — ignore */ }
  }, [debugData]);

  const fetchSearchHistory = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/search/history`, { withCredentials: true });
      setSearchHistory(res.data.items || []);
    } catch { setSearchHistory([]); }
  }, []);

  const doSearch = useCallback(async (q) => {
    if (!q || q.length < 2) { setSearchResults([]); return; }
    setSearchLoading(true);
    try {
      const res = await axios.get(`${API}/search/global?q=${encodeURIComponent(q)}`, { withCredentials: true });
      setSearchResults(res.data.results || []);
    } catch { setSearchResults([]); }
    finally { setSearchLoading(false); }
  }, []);

  const handleSearchInput = (val) => {
    setSearchQuery(val);
    setSelectedIndex(-1);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => doSearch(val), 250);
  };

  const handleSelect = (result) => {
    // Log the selection so it surfaces under "Recent" the next time the
    // search palette is opened (fire-and-forget — UX is unaffected by failure).
    if (result && result.url && result.label) {
      axios.post(`${API}/search/history`, {
        query: searchQuery,
        label: result.label,
        sub: result.sub || '',
        url: result.url,
        type: result.type || '',
        id: result.id || '',
      }, { withCredentials: true }).catch(() => {});
    }
    setSearchOpen(false);
    setSearchQuery('');
    setSearchResults([]);
    navigate(result.url);
  };

  const handleClearHistory = async () => {
    try {
      await axios.delete(`${API}/search/history`, { withCredentials: true });
      setSearchHistory([]);
    } catch { /* non-fatal */ }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(i => Math.min(i + 1, searchResults.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && selectedIndex >= 0 && searchResults[selectedIndex]) {
      e.preventDefault();
      handleSelect(searchResults[selectedIndex]);
    } else if (e.key === 'Escape') {
      setSearchOpen(false);
      setSearchQuery('');
      setSearchResults([]);
    }
  };

  // Keyboard shortcut: Cmd/Ctrl + K
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(true);
        fetchSearchHistory();
        setTimeout(() => searchInputRef.current?.focus(), 50);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [fetchSearchHistory]);

  // Close on click outside
  useEffect(() => {
    const handler = (e) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(e.target)) {
        setSearchOpen(false);
      }
    };
    if (searchOpen) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [searchOpen]);

  // Badge for the nav-bar Alerts entry. Polls /alerts/count once a minute.
  const [alertsCount, setAlertsCount] = useState(0);
  useEffect(() => {
    const tick = async () => {
      try {
        const res = await axios.get(`${API}/alerts/count`, { withCredentials: true });
        setAlertsCount(res.data?.pending || 0);
      } catch {/* silent */}
    };
    tick();
    const iv = setInterval(tick, 60000);
    return () => clearInterval(iv);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="flex h-screen overflow-hidden" data-testid="app-layout">
      {/* Sidebar */}
      <aside
        className={`glass-sidebar flex flex-col shrink-0 transition-all duration-300 ${collapsed ? 'w-[68px]' : 'w-[220px]'}`}
        data-testid="app-sidebar"
      >
        {/* Logo */}
        <div className={`flex items-center h-14 px-4 border-b border-white/10 ${collapsed ? 'justify-center' : 'gap-3'}`}>
          <img
            src="https://customer-assets.emergentagent.com/job_licensing-hub-3/artifacts/0s2i33uj_MM_Forsight%20Logo%20Png.png"
            alt="ForesightRx"
            className={`brightness-0 invert ${collapsed ? 'h-6 w-auto' : 'h-7 w-auto'}`}
          />
        </div>
        {!collapsed && (
          <div className="px-4 py-2 border-b border-white/5">
            <div className="text-[9px] uppercase tracking-widest text-indigo-300/70">Intelligence Workspace</div>
            <div className="text-[8px] text-slate-500 mt-0.5 leading-tight">FIP · AI-Powered Search, Evaluation &amp; Licensing</div>
          </div>
        )}

        {/* Nav */}
        <nav className="flex-1 py-2 px-2 space-y-0.5 overflow-y-auto">
          {NAV_GROUPS.filter((item) => !item.adminOnly || isAdmin).map((item) => {
            const isActive = activeGroupKey(location.pathname) === item.key;
            return (
              <NavLink
                key={item.key}
                to={item.to}
                data-testid={`nav-${item.key}`}
                className={`relative flex items-center gap-2.5 px-2.5 py-2 rounded-md text-xs font-medium transition-all duration-200 ${
                  isActive
                    ? 'bg-[#00709B]/20 text-[#70C24C]'
                    : 'text-slate-400 hover:text-white hover:bg-white/5'
                } ${collapsed ? 'justify-center' : ''}`}
              >
                <item.icon className="w-4 h-4 shrink-0" />
                {!collapsed && <span className="flex-1">{item.label}</span>}
                {item.showBadge && alertsCount > 0 && (
                  <span
                    className={`shrink-0 inline-flex items-center justify-center text-[10px] font-bold rounded-full bg-rose-500 text-white ${collapsed ? 'absolute top-1 right-1 w-4 h-4' : 'min-w-[18px] h-[18px] px-1'}`}
                    data-testid="alerts-badge"
                    title={`${alertsCount} pending alert${alertsCount === 1 ? '' : 's'}`}
                  >
                    {alertsCount > 99 ? '99+' : alertsCount}
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>

        {/* User + Collapse */}
        <div className="border-t border-white/10 p-3 space-y-2">
          {!collapsed && user && (
            <div className="flex items-center gap-2 px-1 mb-2">
              <Avatar className="w-6 h-6">
                <AvatarImage src={user.picture} alt={user.name} />
                <AvatarFallback className="bg-[#00709B] text-white text-[10px]">
                  {user.name?.charAt(0) || 'U'}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-[11px] font-medium text-white truncate">{user.name}</p>
                <p className="text-[9px] text-slate-500 truncate">{user.email}</p>
              </div>
              <button type="button" onClick={() => setPwOpen(true)} className="text-slate-500 hover:text-white transition-colors p-1" title="Set or change your password" data-testid="set-password-btn">
                <KeyRound className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
          <SetPasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
          <div className={`flex ${collapsed ? 'flex-col items-center gap-2' : 'items-center justify-between'}`}>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 text-slate-500 hover:text-red-400 transition-colors text-[11px] px-1 py-1"
              data-testid="logout-btn"
            >
              <LogOut className="w-3.5 h-3.5" />
              {!collapsed && <span>Logout</span>}
            </button>
            <button
              onClick={() => setCollapsed(!collapsed)}
              className="text-slate-500 hover:text-white transition-colors p-1"
              data-testid="collapse-sidebar-btn"
            >
              {collapsed ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronLeft className="w-3.5 h-3.5" />}
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto bg-slate-50 pb-20">
        {/* Read-only viewer banner */}
        {user?.role === 'viewer' && (
          <div className="bg-amber-50 border-b border-amber-200 px-6 py-2 text-[11px] text-amber-800 flex items-center gap-2" data-testid="viewer-mode-banner">
            <span className="font-semibold">Read-only mode</span>
            <span className="text-amber-700">— you can view all data, but edits, bulk emails, and outreach actions are disabled for this account.</span>
          </div>
        )}
        {/* Super Search Bar */}
        <div className="sticky top-0 z-40 bg-slate-50/95 backdrop-blur-sm border-b border-slate-200/60 px-6 py-2.5 flex items-center gap-3">
          <div className="relative flex-1 max-w-xl" ref={searchContainerRef}>
            <div
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border transition-all cursor-text ${
                searchOpen ? 'bg-white border-slate-300 shadow-sm ring-1 ring-slate-200' : 'bg-white/70 border-slate-200 hover:bg-white hover:border-slate-300'
              }`}
              onClick={() => { setSearchOpen(true); fetchSearchHistory(); setTimeout(() => searchInputRef.current?.focus(), 50); }}
              data-testid="global-search-trigger"
            >
              <Search className="w-3.5 h-3.5 text-slate-400 shrink-0" />
              {searchOpen ? (
                <input
                  ref={searchInputRef}
                  value={searchQuery}
                  onChange={e => handleSearchInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Search companies, contacts, assets, emails..."
                  className="flex-1 bg-transparent text-sm text-slate-900 placeholder:text-slate-400 outline-none"
                  data-testid="global-search-input"
                />
              ) : (
                <span className="flex-1 text-sm text-slate-400">Search anything...</span>
              )}
              <kbd className="hidden sm:inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[9px] font-medium text-slate-400 bg-slate-100 rounded border border-slate-200">
                {navigator.platform?.includes('Mac') ? '⌘' : 'Ctrl'}K
              </kbd>
              {isAdmin && searchOpen && searchQuery.length >= 2 && (
                <button
                  onClick={(e) => { e.stopPropagation(); openSearchDebug(); }}
                  className="hidden sm:inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-semibold text-indigo-700 bg-indigo-50 rounded border border-indigo-200 hover:bg-indigo-100"
                  title="Admin: inspect raw resolver output for this query"
                  data-testid="search-debug-btn"
                >
                  <Bug className="w-3 h-3" /> Debug
                </button>
              )}
              {searchOpen && searchQuery && (
                <button onClick={(e) => { e.stopPropagation(); setSearchQuery(''); setSearchResults([]); }} className="text-slate-400 hover:text-slate-600">
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            {/* Search History (shown when palette is open but query is empty/short) */}
            {searchOpen && searchQuery.length < 2 && searchHistory.length > 0 && (
              <div
                className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg border border-slate-200 shadow-lg max-h-[420px] overflow-y-auto z-50"
                data-testid="global-search-history"
              >
                <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100">
                  <span className="text-[10px] uppercase tracking-wide font-semibold text-slate-400">Recent</span>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleClearHistory(); }}
                    className="text-[10px] text-slate-400 hover:text-slate-600"
                    data-testid="global-search-history-clear"
                  >
                    Clear
                  </button>
                </div>
                <div className="py-1">
                  {searchHistory.map((h, i) => {
                    const cfg = TYPE_CONFIG[h.type] || { label: h.type || 'Item', color: 'bg-slate-100 text-slate-600', icon: Building2 };
                    const Icon = cfg.icon;
                    return (
                      <div
                        key={`hist-${i}-${h.url}`}
                        onClick={() => handleSelect(h)}
                        className="w-full flex items-center gap-3 px-3 py-2.5 transition-colors cursor-pointer hover:bg-slate-50"
                        data-testid={`search-history-item-${i}`}
                      >
                        <div className={`w-7 h-7 rounded-md flex items-center justify-center shrink-0 ${cfg.color}`}>
                          <Icon className="w-3.5 h-3.5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-[13px] font-medium text-slate-900 truncate">{h.label}</p>
                          {h.sub && <p className="text-[11px] text-slate-400 truncate">{h.sub}</p>}
                        </div>
                        <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded shrink-0 ${cfg.color}`}>{cfg.label}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Search Results Dropdown */}
            {searchOpen && (searchQuery.length >= 2) && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg border border-slate-200 shadow-lg max-h-[420px] overflow-y-auto z-50" data-testid="global-search-results">
                {searchLoading ? (
                  <div className="px-4 py-6 text-center">
                    <div className="w-5 h-5 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin mx-auto" />
                  </div>
                ) : searchResults.length === 0 ? (
                  <div className="px-4 py-6 text-center text-sm text-slate-400">No results found for "{searchQuery}"</div>
                ) : (
                  <div className="py-1">
                    {searchResults.map((r, i) => {
                      const primaryConfig = TYPE_CONFIG[r.type] || { label: r.type, color: 'bg-slate-100 text-slate-600', icon: Building2 };
                      const Icon = primaryConfig.icon;
                      const allTypes = r.all_types || [r.type];
                      const links = r.links || [{ type: r.type, url: r.url, id: r.id }];
                      return (
                        <div
                          key={`${r.type}-${r.id}-${i}`}
                          onMouseEnter={() => setSelectedIndex(i)}
                          className={`w-full flex items-center gap-3 px-3 py-2.5 transition-colors cursor-pointer ${
                            selectedIndex === i ? 'bg-slate-50' : 'hover:bg-slate-50'
                          }`}
                          onClick={() => handleSelect(r)}
                          data-testid={`search-result-${r.type}-${r.id}`}
                        >
                          <div className={`w-7 h-7 rounded-md flex items-center justify-center shrink-0 ${primaryConfig.color}`}>
                            <Icon className="w-3.5 h-3.5" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-[13px] font-medium text-slate-900 truncate">{r.label}</p>
                            {r.sub && <p className="text-[11px] text-slate-400 truncate">{r.sub}</p>}
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            {links.length > 1 ? (
                              links.map((link, li) => {
                                const cfg = TYPE_CONFIG[link.type] || { label: link.type, color: 'bg-slate-100 text-slate-600' };
                                return (
                                  <button
                                    key={li}
                                    onClick={(e) => { e.stopPropagation(); handleSelect({ url: link.url }); }}
                                    className={`text-[9px] font-semibold px-1.5 py-0.5 rounded hover:ring-1 hover:ring-slate-300 transition-all ${cfg.color}`}
                                    title={`Go to ${cfg.label}`}
                                  >
                                    {cfg.label}
                                  </button>
                                );
                              })
                            ) : (
                              <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded ${primaryConfig.color}`}>{primaryConfig.label}</span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        <SubTabBar />
        {children}
      </main>
      {debugOpen && (
        <div
          className="fixed inset-0 z-[60] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setDebugOpen(false)}
          data-testid="search-debug-modal"
        >
          <div
            className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 min-w-0">
                <Bug className="w-4 h-4 text-indigo-600 shrink-0" />
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-wide text-indigo-700 font-semibold">Search Resolver Debug</div>
                  <div className="text-xs text-slate-600 truncate">Query: <span className="font-mono">{searchQuery}</span></div>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={copyDebugJson}
                  disabled={!debugData}
                  className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded border border-slate-200 disabled:opacity-50"
                  data-testid="search-debug-copy"
                >
                  <Copy className="w-3 h-3" /> Copy JSON
                </button>
                <button
                  onClick={() => setDebugOpen(false)}
                  className="text-slate-400 hover:text-slate-700 p-1"
                  aria-label="Close"
                  data-testid="search-debug-close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-4 text-xs">
              {debugLoading ? (
                <div className="text-slate-500 text-center py-10">Loading…</div>
              ) : debugError ? (
                <div className="text-rose-700">{debugError}</div>
              ) : debugData ? (
                <>
                  <div className="mb-3 p-2 rounded bg-slate-50 border border-slate-200">
                    <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold mb-1">Resolver would route to</div>
                    {debugData.resolver_result?.url ? (
                      // Feb 2026 · make the resolver URL clickable so
                      // clicking it closes the modal + navigates
                      // straight to the target (staging row via
                      // ?focus=<id>, canonical company profile, etc.).
                      // Previously this was plain <div> text and users
                      // had to copy/paste — user report "there is no
                      // URL which is clickable".
                      <button
                        type="button"
                        onClick={() => {
                          const url = debugData.resolver_result.url;
                          setDebugOpen(false);
                          navigate(url);
                        }}
                        className="font-mono text-indigo-700 hover:text-indigo-900 hover:underline break-all text-left w-full cursor-pointer"
                        data-testid="search-debug-resolver-url"
                        title="Click to navigate + close this debug panel"
                      >
                        {debugData.resolver_result.url}
                      </button>
                    ) : (
                      <div className="font-mono text-rose-600 break-all" data-testid="search-debug-resolver-url">
                        null (no canonical, no staging, no asset link)
                      </div>
                    )}
                  </div>
                  <pre className="bg-slate-900 text-slate-100 rounded p-3 text-[10px] leading-snug overflow-x-auto" data-testid="search-debug-json">
{JSON.stringify(debugData, null, 2)}
                  </pre>
                </>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
